import {
  Button,
  ButtonColor,
  ButtonSize,
  FileUpload,
  TextInput,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { ModelError } from 'src/front/api/generated';
import previewIcon from 'src/front/assets/icon_preview.svg';
import { ACCEPTED_FILE_UPLOAD_FORMATS } from 'src/front/common/constants/files';
import { isInvalidFileError } from 'src/front/common/utils/errorUtils';
import { checkAcceptedFileType, openDocument } from 'src/front/common/utils/fileUploadUtils';

import { formatAlphaNumericCyrillicSpecialCharactersWithoutQuotes } from 'src/front/common/utils/formUtils';
import { DocumentUploadSchemaWithoutDocumentType } from 'src/front/common/utils/validationSchemas';

import {
  ButtonRow,
  FormElement,
  FormRow,
  Label,
  ResetButton,
  Separator,
} from 'src/front/components/FormStyles';
import styled from 'styled-components';
import FormModal from './FormModal';

export const StyledFormRow = styled(FormRow)`
  width: 100%;

  & > div {
    width: 100%;
  }
`;
export const StyledPreviewButton = styled.button`
  position: relative;

  border: none;
  margin-right: 20px;

  background: none;

  &&:focus {
    outline: none;
  }
`;

export interface PlasticCardDocumentUploadFormData {
  files?: FileList | null;
  filePath?: string;
  explanation: string;
  isOldDocument: boolean;
  createDate: string;
  documentId: string;
  documentName: string;
}

const defaultValues = {
  files: undefined,
  filePath: '',
  explanation: '',
  isOldDocument: false,
  createDate: '',
  documentId: '',
  documentName: '',
};

interface Props {
  submit: (data: PlasticCardDocumentUploadFormData) => void;
  error?: ModelError;
  onClose: () => void;
  formValues?: PlasticCardDocumentUploadFormData;
}

const PlasticCardDocumentUploadForm: FC<Props> = ({ submit, error, onClose, formValues }) => {
  const { t } = useTranslation();
  const [previewAvailable, setPreviewAvailable] = useState<boolean>(false);
  const {
    watch,
    register,
    errors,
    setValue,
    handleSubmit,
    control,
    formState,
    setError,
    getValues,
    reset,
  } = useForm<PlasticCardDocumentUploadFormData>({
    defaultValues: {
      ...defaultValues,
      ...formValues,
    },
    resolver: yupResolver(DocumentUploadSchemaWithoutDocumentType),
  });

  const registerFields = useCallback(() => {
    register({ name: 'files' });
    register({ name: 'filePath' });
    register({ name: 'documentId' });
  }, [register]);

  useEffect(() => {
    if (error && isInvalidFileError(error)) {
      setError('files', { type: 'required', message: 'general_uploadFileFormat_errorMessage' });
    }
  }, [error, setError]);

  useEffect(() => {
    registerFields();
  }, [registerFields]);

  const handleFileSelect = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setValue('files', target.files, { shouldValidate: true, shouldDirty: true });
    setValue('filePath', target.value);
    setPreviewConfiguration(target.files);
  };
  const onPreview = (): void => {
    const { files } = getValues();
    openDocument(files);
  };

  const setPreviewConfiguration = async (data?: FileList | null): Promise<void> => {
    const acceptStatus = await checkAcceptedFileType(data);
    setPreviewAvailable(acceptStatus);
  };
  const onSubmit = (data: PlasticCardDocumentUploadFormData): void => {
    submit({ ...formValues, ...data });
  };

  const onReset = (): void => {
    reset();
    registerFields();
  };

  const isEditMode = Boolean(formValues?.documentId);

  return (
    <FormModal onClose={onClose} size="350px" zIndex={200}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Label>
          <Trans i18nKey="cardEntry_documentEntry_uploadDocument_text" />
        </Label>
        <FormRow>
          <FormElement>
            <FileUpload
              accept={ACCEPTED_FILE_UPLOAD_FORMATS}
              description={t('general_uploadFile_text')}
              disabled={isEditMode}
              errorMessage={t(errors?.files?.message as string)}
              label={t('general_uploadDocument_label')}
              name="file"
              onChange={handleFileSelect}
              value={watch('filePath')}
              required
            >
              {previewAvailable ? (
                <StyledPreviewButton onClick={onPreview} type="button">
                  <img alt="previewIcon" src={previewIcon} />
                </StyledPreviewButton>
              ) : null}
            </FileUpload>
          </FormElement>
        </FormRow>
        <StyledFormRow>
          <Controller
            control={control}
            name="explanation"
            render={({ onChange, value }) => (
              <TextInput
                errorMessage={t(errors?.explanation?.message as string)}
                formatter={formatAlphaNumericCyrillicSpecialCharactersWithoutQuotes}
                label={t('general_note_label')}
                maxLength={50}
                name="explanation"
                onChange={onChange}
                value={value}
              />
            )}
          />
        </StyledFormRow>
        <Separator />
        <ButtonRow>
          <ResetButton
            color={ButtonColor.Secondary}
            onClick={onReset}
            size={ButtonSize.Medium}
            text={t('general_components_reset_button')}
            type="reset"
          />

          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_save_button')}
            type="submit"
          />
        </ButtonRow>
      </form>
    </FormModal>
  );
};

export default PlasticCardDocumentUploadForm;
